import type { ReactNode, ElementType } from 'react';
import { useState } from 'react';
import AnimateHeight from 'react-animate-height';

import './Expandable.scss';

interface Props {
  header: ReactNode;
  children: ReactNode;
  element: ElementType;
  className?: string;
  expanded?: boolean;
  inverseExpanded: () => void;
  disableDisplayNone?: boolean;
}

export const Expandable = (props: Omit<Props, 'expanded' | 'inverseExpanded'>) => {
  const [expanded, setExpanded] = useState<boolean>(false);
  const handleToggle = () => {
    setExpanded((prevExpanded: boolean) => !prevExpanded);
  };

  const { header, children, element: Element, className } = props;

  return (
    <Element className={`expandable ${expanded ? 'expanded' : ''} ${className || ''}`}>
      <div
        className="meta"
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        onClick={handleToggle}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleToggle();
          }
        }}
      >
        {header}
      </div>
      <AnimateHeight className="details" height={expanded ? 'auto' : 0}>
        {children}
      </AnimateHeight>
    </Element>
  );
};

export const ControlledExpandable = (props: Props) => {
  const { header, children, element: Element, className } = props;

  return (
    <Element className={`expandable ${props.expanded ? 'expanded' : ''} ${className || ''}`}>
      <div
        className="meta"
        role="button"
        tabIndex={0}
        aria-expanded={props.expanded ?? false}
        onClick={props.inverseExpanded}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            props.inverseExpanded();
          }
        }}
      >
        {header}
      </div>
      <AnimateHeight
        className="details"
        height={props.expanded ? 'auto' : 0}
        disableDisplayNone={props.disableDisplayNone}
      >
        {children}
      </AnimateHeight>
    </Element>
  );
};
